/**
 * @fileoverview HREF Sort Preference Interceptor
 *
 * =============================================================================
 * PURPOSE & OVERVIEW
 * =============================================================================
 *
 * This script manages user sort preferences across navigation links. When a user
 * selects a sort option (e.g., "Price: High to Low") on a product listing page,
 * that preference is saved to localStorage. This script then applies that preference
 * to navigation links so the user sees their preferred sort when clicking through
 * to other listing pages.
 *
 * HOWEVER, not all pages support the same sort options. For example:
 *   - /stock/landscapes/ might allow: high_to_low_price, low_to_high_price, artist_name_asc, etc.
 *   - /sold/stock/landscapes/ might only allow: artist_name_asc, artist_name_desc, title_asc, etc.
 *     (no price sorting because sold items don't have prices)
 *
 * This script validates the user's preference against an "allowed sorts" configuration
 * fetched from the server, ensuring we never apply an invalid sort to a URL.
 *
 * =============================================================================
 * KEY DESIGN DECISIONS
 * =============================================================================
 *
 * 1. FAIL CLOSED PRINCIPLE
 *    If we can't validate a sort preference, we DON'T apply it. This prevents
 *    users from landing on error pages due to invalid sort parameters.
 *
 * 2. NO DEFAULT SORT FALLBACK
 *    If a user's preference isn't allowed for a destination URL, we leave the
 *    URL unchanged rather than applying a "default" sort. This respects the
 *    original link's intent (which may have its own sort parameter).
 *
 * 3. URL PATH NORMALIZATION
 *    Development URLs often have prefixes like /websites/site-name/ that don't
 *    exist in production. We strip these for consistent matching.
 *
 * 4. TWO-PHASE URL MODIFICATION
 *    - On page load: Update href attributes so right-click "Open in new tab" works
 *    - On click: Intercept and validate again (handles dynamic content)
 *
 * =============================================================================
 * VALIDATION FLOW
 * =============================================================================
 *
 * When processing a link like <a href="/sold/stock/landscapes/?sort=artist_name_asc" data-apply-sort-pref>:
 *
 * 1. Check: Has allowedSorts config been fetched?
 *    - NO  -> Return original URL (fail closed)
 *    - YES -> Continue
 *
 * 2. Check: Does destination URL have ANY sort config?
 *    - NO  -> Return original URL (page doesn't support sorting, e.g., Archives)
 *    - YES -> Continue
 *
 * 3. Check: Is user's saved preference in the allowed list for this URL?
 *    - NO  -> Return original URL (preference not valid for this page)
 *    - YES -> Apply preference to URL
 *
 * =============================================================================
 * USAGE
 * =============================================================================
 *
 * @example
 * // Basic usage - tag links that should have sort preference applied:
 * <a href="/products?category=art" data-apply-sort-pref>View Products</a>
 *
 * @example
 * // With specific grid ID (for multi-grid pages):
 * <a href="/items" data-apply-sort-pref="featured">Featured Items</a>
 *
 * @example
 * // Multiple list types with data-sort-type:
 * <a href="/products" data-apply-sort-pref="grid1" data-sort-type="product">Products</a>
 * <a href="/makers" data-apply-sort-pref="grid1" data-sort-type="maker">Makers</a>
 *
 * @example
 * // Enable debug mode for troubleshooting:
 * window.HrefSortInterceptor.setDebugMode(true);
 *
 * @requires url_param_handler.js (optional, but preferred for URL manipulation)
 */

/**
 * @typedef {Object} AllowedSortConfig
 * @property {string} list_url - The URL path this configuration applies to (e.g., "/stock/landscapes/")
 * @property {string[]} allowed_sort_ids - Array of allowed sort IDs for this URL
 */

/**
 * @typedef {Object} PreferenceCache
 * @property {string|null} value - The cached preference value
 * @property {number} timestamp - When this was cached (milliseconds since epoch)
 */

window.HrefSortInterceptor = window.HrefSortInterceptor || {
  /** @type {boolean} */
  debugMode: false,

  /** @type {Record<string, PreferenceCache>} */
  preferenceCache: {},

  /** @type {number} Cache TTL in milliseconds (5 seconds for in-memory preference cache) */
  cacheTTL: 5000,

  /** @type {AllowedSortConfig[]|null} */
  allowedSorts: null,

  /** @type {boolean} */
  allowedSortsFetched: false,

  /** @type {Promise<void>|null} - Promise that resolves when allowedSorts is ready */
  _allowedSortsReadyPromise: null,

  /** @type {Function|null} - Resolver for the ready promise */
  _allowedSortsReadyResolver: null,

  /**
   * Enable or disable debug logging
   * @param {boolean} enabled - Whether to enable debug mode
   * @returns {void}
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
    this.log(`Debug mode ${enabled ? "ENABLED" : "DISABLED"}`);
  },

  /**
   * Get a promise that resolves when allowedSorts configuration is ready.
   *
   * WHY: Other scripts (like UrlParamHandler) need to wait for this config
   * before processing links, otherwise they might apply invalid sorts.
   *
   * @returns {Promise<void>}
   */
  whenReady() {
    if (this.allowedSortsFetched) {
      return Promise.resolve();
    }

    if (!this._allowedSortsReadyPromise) {
      this._allowedSortsReadyPromise = new Promise((resolve) => {
        this._allowedSortsReadyResolver = resolve;
      });
    }

    return this._allowedSortsReadyPromise;
  },

  /**
   * Signal that allowedSorts is ready - resolves any pending whenReady() promises
   * @private
   * @returns {void}
   */
  _signalReady() {
    this.allowedSortsFetched = true;
    if (this._allowedSortsReadyResolver) {
      this._allowedSortsReadyResolver();
      this._allowedSortsReadyResolver = null;
    }
  },

  /**
   * Internal logging - only logs when debugMode is enabled
   * @param {...*} args - Arguments to log
   * @returns {void}
   */
  log(...args) {
    if (this.debugMode) {
      console.log("[HrefSort]", ...args);
    }
  },

  /**
   * Fetch allowed sorts configuration from API.
   *
   * WHY WE NEED THIS:
   * Different pages have different valid sort options. A "sold items" page can't
   * sort by price (no prices on sold items), while a "for sale" page can.
   * The server provides a mapping of URL -> allowed sorts.
   *
   * CACHING STRATEGY:
   * - localStorage cache with 1 hour TTL (reduces API calls)
   * - Falls back to expired cache if API fails (graceful degradation)
   * - In-memory storage for current session
   *
   * @param {boolean} [forceRefresh=false] - Force fetch even if cache is valid
   * @returns {Promise<void>}
   */
  async fetchAllowedSorts(forceRefresh = false) {
    const CACHE_DURATION = 3600000; // 1 hour
    this.log("Fetching allowed sorts configuration");

    try {
      // Check localStorage cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = window.siteStorage.get("allowed_sorts");
        const cacheTimestamp = window.siteStorage.get(
          "allowed_sorts_timestamp",
        );
        const cacheAge = cacheTimestamp
          ? Date.now() - parseInt(cacheTimestamp, 10)
          : Infinity;

        this.log("Cache check:", {
          hasCached: !!cached,
          cacheAge:
            cacheAge === Infinity
              ? "no timestamp"
              : `${Math.floor(cacheAge / 1000)}s`,
          isValid: cached && cacheAge < CACHE_DURATION,
        });

        if (cached && cacheAge < CACHE_DURATION) {
          this.log("Using cached allowed sorts");
          this.allowedSorts = cached; // already parsed
          this._signalReady();
          return;
        }
      }

      // Fetch from API using workflow endpoint
      this.log("Fetching from API:", window.location.pathname);

      const response = await fetch(window.location.pathname, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          workflows: ["get_allowed_sorts"],
          jinja_data: {},
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      this.log("Allowed sorts fetched successfully:", data);

      // Store in memory and cache to localStorage
      this.allowedSorts = data;
      this._signalReady();
      window.siteStorage.set("allowed_sorts", data);
      window.siteStorage.set("allowed_sorts_timestamp", Date.now());
      this.log("Cached allowed sorts to localStorage");
    } catch (error) {
      this.log("Error fetching allowed sorts:", error.message);

      // Fallback to expired cache if available - better than nothing
      const cached = window.siteStorage.get("allowed_sorts");
      if (cached) {
        this.log("Using expired cache as fallback");
        this.allowedSorts = cached;
        this._signalReady();
      } else {
        // No cache available - we'll deny all sort applications (fail closed)
        this.log("No cache available, denying unknown sorts by default");
        this._signalReady();
      }
    }
  },

  /**
   * Normalize URL path by removing /websites/{x}/ prefix if present.
   *
   * WHY: In development, URLs have prefixes like /websites/karen-taylor-fine-art/
   * but the allowedSorts config uses production paths like /stock/landscapes/.
   * We need to strip the prefix for matching.
   *
   * @param {string} urlPath - The URL path to normalize
   * @returns {string} Normalized URL path
   */
  normalizeUrlPath(urlPath) {
    // Remove /websites/{site_name}/ prefix if present
    // Pattern: /websites/anything/ at the start
    const normalized = urlPath.replace(/^\/websites\/[^/]+\//, "/");

    if (normalized !== urlPath) {
      this.log(`Normalized URL path: ${urlPath} -> ${normalized}`);
    }

    return normalized;
  },

  /**
   * Check if a URL path has ANY sort configuration (i.e., supports sorting at all).
   *
   * WHY THIS EXISTS:
   * Some pages don't support sorting at all (e.g., "Archives", "About Us").
   * If a URL isn't in our config, we should leave it completely unchanged
   * rather than trying to apply or validate sorts.
   *
   * @param {string} urlPath - The URL path to check
   * @returns {boolean} Whether this URL has sort configuration
   */
  hasSortConfigForUrl(urlPath) {
    // Can't check if config not loaded
    if (
      !this.allowedSortsFetched ||
      !this.allowedSorts ||
      !Array.isArray(this.allowedSorts)
    ) {
      return false;
    }

    const normalizedPath = this.normalizeUrlPath(urlPath);
    const config = this.allowedSorts.find(
      (item) => item.list_url === normalizedPath,
    );

    return !!config;
  },

  /**
   * Check if a specific sort ID is allowed for a given URL path.
   *
   * VALIDATION LOGIC:
   * 1. If config not fetched -> return false (fail closed)
   * 2. If URL not in config -> return false (page doesn't support this sort)
   * 3. If sort in allowed list -> return true
   * 4. Otherwise -> return false
   *
   * IMPORTANT: Only call this after allowedSortsFetched is true.
   * For async contexts, use isSortAllowedForUrlAsync instead.
   *
   * @param {string} urlPath - The URL path (e.g., '/stock/landscapes/' or '/websites/karen-taylor/stock/landscapes/')
   * @param {string} sortId - The sort ID to validate (e.g., 'high_to_low_price')
   * @returns {boolean} Whether the sort is allowed
   */
  isSortAllowedForUrl(urlPath, sortId) {
    // If config hasn't been fetched yet, fail closed (don't apply sort)
    if (!this.allowedSortsFetched) {
      this.log("Allowed sorts not yet fetched, denying sort application");
      return false;
    }

    // If we have no config at all (fetch failed with no cache), fail closed
    if (!this.allowedSorts || !Array.isArray(this.allowedSorts)) {
      this.log("No allowed sorts config available, denying sort application");
      return false;
    }

    // Normalize the URL path to remove /websites/{x}/ prefix
    const normalizedPath = this.normalizeUrlPath(urlPath);

    // Find config matching the normalized path
    const config = this.allowedSorts.find(
      (item) => item.list_url === normalizedPath,
    );

    if (!config) {
      // URL not in config - page doesn't support sorting or sort not configured
      this.log(
        `No config for ${normalizedPath} (original: ${urlPath}), denying by default`,
      );
      return false;
    }

    // Check if the specific sort ID is in the allowed list
    const isAllowed = config.allowed_sort_ids.includes(sortId);
    this.log(
      `Sort '${sortId}' is ${isAllowed ? "allowed" : "NOT allowed"} for ${normalizedPath}`,
    );

    return isAllowed;
  },

  /**
   * Get the default (first) allowed sort for a URL path.
   *
   * NOTE: Currently not used for fallback behavior (we leave URLs unchanged
   * instead of applying defaults), but kept for potential future use and diagnostics.
   *
   * @param {string} urlPath - The URL path
   * @returns {string|null} The first allowed sort ID, or null if none found
   */
  getDefaultSortForUrl(urlPath) {
    if (
      !this.allowedSortsFetched ||
      !this.allowedSorts ||
      !Array.isArray(this.allowedSorts)
    ) {
      this.log("No allowed sorts config available");
      return null;
    }

    const normalizedPath = this.normalizeUrlPath(urlPath);
    const config = this.allowedSorts.find(
      (item) => item.list_url === normalizedPath,
    );

    if (
      !config ||
      !config.allowed_sort_ids ||
      config.allowed_sort_ids.length === 0
    ) {
      this.log(`No default sort found for ${normalizedPath}`);
      return null;
    }

    const defaultSort = config.allowed_sort_ids[0];
    this.log(`Default sort for ${normalizedPath}: ${defaultSort}`);
    return defaultSort;
  },

  /**
   * Get all allowed sorts for a URL path (useful for building sort dropdowns)
   * @param {string} urlPath - The URL path
   * @returns {string[]} Array of allowed sort IDs, or empty array if none found
   */
  getAllowedSortsForUrl(urlPath) {
    if (
      !this.allowedSortsFetched ||
      !this.allowedSorts ||
      !Array.isArray(this.allowedSorts)
    ) {
      return [];
    }

    const normalizedPath = this.normalizeUrlPath(urlPath);
    const config = this.allowedSorts.find(
      (item) => item.list_url === normalizedPath,
    );

    return config?.allowed_sort_ids || [];
  },

  /**
   * Async version of isSortAllowedForUrl - waits for config to be ready first
   * @param {string} urlPath - The URL path
   * @param {string} sortId - The sort ID to validate
   * @returns {Promise<boolean>} Whether the sort is allowed
   */
  async isSortAllowedForUrlAsync(urlPath, sortId) {
    await this.whenReady();
    return this.isSortAllowedForUrl(urlPath, sortId);
  },

  /**
   * Initialize the interceptor - fetches config and sets up click handler
   * @returns {Promise<void>}
   */
  async init() {
    this.log("Initializing interceptor");

    // Fetch allowed sorts configuration first
    await this.fetchAllowedSorts();

    // Set up event delegation for click interception
    // Using capture phase (true) to intercept before other handlers
    document.addEventListener("click", this.handleLinkClick.bind(this), true);

    const taggedLinks = document.querySelectorAll("a[data-apply-sort-pref]");
    this.log(`Initialized - found ${taggedLinks.length} tagged link(s)`);

    if (this.debugMode && taggedLinks.length > 0) {
      this.log(
        "Tagged links:",
        Array.from(taggedLinks).map((l) => l.href),
      );
    }
  },

  /**
   * Handle click events on links with data-apply-sort-pref attribute.
   *
   * WHY CLICK INTERCEPTION:
   * While we update href attributes on page load, dynamic content (HTMX, etc.)
   * might add new links. Click interception ensures we always validate and
   * apply preferences at navigation time.
   *
   * @param {MouseEvent} event - The click event
   * @returns {void}
   */
  handleLinkClick(event) {
    // Find the closest <a> tag (handles clicks on child elements like icons/spans)
    const link = event.target.closest("a[data-apply-sort-pref]");

    if (!link) {
      return; // Not a tagged link, let it pass through
    }

    const originalHref = link.getAttribute("href");
    this.log("Click detected on tagged link:", originalHref);

    // Skip invalid hrefs (empty, hash-only)
    if (!originalHref || originalHref === "#" || originalHref.startsWith("#")) {
      this.log("Skipping - no href or hash link");
      return;
    }

    // Skip external links - we only manage internal navigation
    try {
      const linkUrl = new URL(originalHref, window.location.origin);

      if (linkUrl.origin !== window.location.origin) {
        this.log("Skipping - external link");
        return;
      }
    } catch (e) {
      console.warn("[HrefSort] Invalid URL:", originalHref, e);
      return;
    }

    // Get configuration from data attributes
    const gridId = link.getAttribute("data-apply-sort-pref") || "default";
    const sortType = link.getAttribute("data-sort-type") || "product";

    this.log(`Processing - gridId: ${gridId}, sortType: ${sortType}`);

    // Apply user's sort preference (with validation)
    const updatedUrl = this.applyUserSortToUrl(originalHref, gridId, sortType);

    // If URL changed, intercept navigation and use updated URL
    if (updatedUrl !== originalHref) {
      this.log("Intercepting navigation:", {
        originalHref,
        updatedUrl,
      });

      event.preventDefault();
      event.stopPropagation();

      // Dispatch custom event so other scripts can react/cancel if needed
      const customEvent = new CustomEvent("hrefSortIntercepted", {
        detail: { originalHref, updatedUrl, gridId, sortType, link },
        bubbles: true,
        cancelable: true,
      });

      const eventNotCancelled = link.dispatchEvent(customEvent);

      if (eventNotCancelled) {
        window.location.href = updatedUrl;
      } else {
        this.log("Navigation cancelled by event listener");
      }
    } else {
      this.log("URL unchanged, proceeding normally");
    }
  },

  /**
   * Get cached preference or read from localStorage.
   *
   * CACHING STRATEGY:
   * We cache preferences in memory for 5 seconds to avoid repeated localStorage
   * reads when processing multiple links. This is especially helpful during
   * HTMX swaps that might trigger multiple processSortPrefLinks calls.
   *
   * @param {string} gridId - The grid ID
   * @param {string} [sortType='product'] - The sort type
   * @returns {string|null} The saved preference or null
   */
  getCachedPreference(gridId, sortType = "product") {
    const now = Date.now();
    const cacheKey = `${sortType}_${gridId}`;
    const cached = this.preferenceCache[cacheKey];

    // Return cached value if still valid
    if (cached && now - cached.timestamp < this.cacheTTL) {
      this.log(
        `Using cached preference for ${sortType}/${gridId}:`,
        cached.value,
      );
      return cached.value;
    }

    // Read from localStorage using siteStorage for consistent key prefixing
    // Key pattern: {sortType}_grid_sort_user_pref_{gridId}
    const storageKey = `${sortType}_grid_sort_user_pref_${gridId}`;

    try {
      const value = window.siteStorage.get(storageKey);
      this.log(`Read from siteStorage [${storageKey}]:`, value);

      // Cache the value for subsequent reads
      this.preferenceCache[cacheKey] = { value, timestamp: now };

      return value;
    } catch (e) {
      console.warn("[HrefSort] Could not access siteStorage:", e);
      return null;
    }
  },

  /**
   * Clear preference cache (useful when user changes sort preference)
   * @returns {void}
   */
  clearCache() {
    this.preferenceCache = {};
    this.log("Preference cache cleared");
  },

  /**
   * Clear allowed sorts cache and optionally refetch
   * @param {boolean} [refetch=false] - Whether to refetch after clearing
   * @returns {Promise<void>}
   */
  async clearAllowedSortsCache(refetch = false) {
    try {
      window.siteStorage.remove("allowed_sorts");
      window.siteStorage.remove("allowed_sorts_timestamp");
      this.allowedSorts = null;
      this.allowedSortsFetched = false;
      this.log("Allowed sorts cache cleared");

      if (refetch) {
        await this.fetchAllowedSorts(true);
      }
    } catch (e) {
      console.warn("[HrefSort] Could not clear allowed sorts cache:", e);
    }
  },

  /**
   * Apply user's saved sort preference to a URL.
   *
   * This is the main entry point for URL modification. It delegates to
   * UrlParamHandler if available (preferred), otherwise uses basicApplySort.
   *
   * @param {string} url - The original URL from href
   * @param {string} gridId - The grid ID for localStorage lookup
   * @param {string} [sortType='product'] - The sort type
   * @returns {string} Updated URL with sort applied (or original if no change needed)
   */
  applyUserSortToUrl(url, gridId, sortType = "product") {
    this.log(
      `applyUserSortToUrl - URL: ${url}, gridId: ${gridId}, sortType: ${sortType}`,
    );

    // SAFETY CHECK: Don't apply preferences until we have validation data
    // This prevents race conditions where sorts get applied before we know what's valid
    if (!this.allowedSortsFetched) {
      this.log("Allowed sorts not yet fetched, skipping sort application");
      return url;
    }

    try {
      // Use UrlParamHandler if available (preferred method with full validation)
      if (window.UrlParamHandler) {
        this.log("Using UrlParamHandler.applyUserSortPreference");
        return window.UrlParamHandler.applyUserSortPreference(url, gridId, {
          forceApply: false,
          resetPage: true,
          sortType,
        });
      }

      // Fallback to basic implementation if UrlParamHandler not loaded
      this.log("UrlParamHandler not found, using basicApplySort");
      return this.basicApplySort(url, gridId, sortType);
    } catch (error) {
      console.error("[HrefSort] Error applying sort preference:", error);
      return url;
    }
  },

  /**
   * Basic fallback implementation if UrlParamHandler is not available.
   *
   * VALIDATION FLOW:
   * 1. Check if destination URL has any sort config
   *    - NO  -> Return original URL (page doesn't support sorting)
   * 2. Get user's saved preference from cache/localStorage
   *    - NONE -> Return original URL (no preference to apply)
   * 3. Check if preference is allowed for destination URL
   *    - NO  -> Return original URL (preference not valid, don't guess a default)
   *    - YES -> Apply preference and return updated URL
   *
   * @param {string} url - The URL to update
   * @param {string} gridId - The grid ID for localStorage
   * @param {string} [sortType='product'] - The sort type
   * @returns {string} Updated URL or original
   */
  basicApplySort(url, gridId, sortType = "product") {
    this.log("basicApplySort called");

    try {
      const urlObj = new URL(url, window.location.origin);
      const params = new URLSearchParams(urlObj.search);
      const urlPath = urlObj.pathname;
      const currentSort = params.get("sort");

      // STEP 1: Check if this URL has sort configuration at all
      // If not in config, this page doesn't support sorting - leave URL unchanged
      // Example: Archives, About Us, Contact pages
      if (!this.hasSortConfigForUrl(urlPath)) {
        this.log(`No sort config for ${urlPath}, leaving URL unchanged`);
        return url;
      }

      // STEP 2: Get user's saved sort preference
      const userPreference = this.getCachedPreference(gridId, sortType);

      if (userPreference) {
        this.log(`Found user preference: ${userPreference}`);

        // STEP 3: Validate sort is allowed for this URL
        if (this.isSortAllowedForUrl(urlPath, userPreference)) {
          // Preference is valid - apply it if different from current sort
          if (currentSort !== userPreference) {
            this.log(`Applying user preference: ${userPreference}`);
            params.set("sort", userPreference);
            params.set("page", "1"); // Reset to page 1 when sort changes
            urlObj.search = params.toString();
            return urlObj.href;
          } else {
            this.log("URL already has user's preferred sort");
          }
        } else {
          // User preference NOT allowed for this URL
          // IMPORTANT: We leave the URL unchanged rather than applying a "default"
          // This respects the original link's intent and prevents unexpected behavior
          this.log(
            `User preference '${userPreference}' not allowed for ${urlPath}, leaving URL unchanged`,
          );
        }
      } else {
        this.log("No user preference found");
      }

      return url;
    } catch (error) {
      console.error("[HrefSort] Error in basicApplySort:", error);
      return url;
    }
  },

  /**
   * Update all tagged links on the page (batch update).
   * Called after HTMX swaps to process new content.
   *
   * @param {string} [selector='a[data-apply-sort-pref]'] - CSS selector
   * @returns {number} Number of links updated
   */
  updateAllLinks(selector = "a[data-apply-sort-pref]") {
    let updateCount = 0;

    try {
      const links = document.querySelectorAll(selector);

      links.forEach((link) => {
        const originalHref = link.getAttribute("href");
        if (!originalHref || originalHref === "#") return;

        const gridId = link.getAttribute("data-apply-sort-pref") || "default";
        const sortType = link.getAttribute("data-sort-type") || "product";
        const updatedUrl = this.applyUserSortToUrl(
          originalHref,
          gridId,
          sortType,
        );

        if (updatedUrl !== originalHref) {
          link.setAttribute("href", updatedUrl);
          updateCount++;
        }
      });

      this.log(`Updated ${updateCount} link(s)`);
    } catch (error) {
      console.error("[HrefSort] Error updating links:", error);
    }

    return updateCount;
  },

  /**
   * Clear user's sort preference for a specific grid
   * @param {string} [gridId='default'] - The grid ID to clear
   * @param {string} [sortType='product'] - The sort type to clear
   * @returns {void}
   */
  clearUserSortPreference(gridId = "default", sortType = "product") {
    try {
      const storageKey = `${sortType}_grid_sort_user_pref_${gridId}`;
      window.siteStorage.remove(storageKey);

      // Clear from in-memory cache as well
      const cacheKey = `${sortType}_${gridId}`;
      delete this.preferenceCache[cacheKey];

      this.log(`Cleared sort preference for ${sortType}/${gridId}`);
    } catch (e) {
      console.warn("[HrefSort] Could not clear sort preference:", e);
    }
  },

  /**
   * Get diagnostic information for troubleshooting
   * @returns {Object} Diagnostic info
   */
  getDiagnostics() {
    const diagnostics = {
      scriptsLoaded: {
        UrlParamHandler: typeof window.UrlParamHandler !== "undefined",
        HrefSortInterceptor: typeof window.HrefSortInterceptor !== "undefined",
        urlUpdateSortID: typeof window.urlUpdateSortID !== "undefined",
      },
      readinessState: {
        allowedSortsFetched: this.allowedSortsFetched,
        hasAllowedSorts: !!this.allowedSorts,
        allowedSortsCount: this.allowedSorts ? this.allowedSorts.length : 0,
        hasPendingPromise: !!this._allowedSortsReadyPromise,
      },
      taggedLinksCount: document.querySelectorAll("a[data-apply-sort-pref]")
        .length,
      preferences: {},
      cacheSize: Object.keys(this.preferenceCache).length,
      debugMode: this.debugMode,
      // Legacy fields for backward compatibility
      allowedSortsFetched: this.allowedSortsFetched,
      allowedSortsCount: this.allowedSorts ? this.allowedSorts.length : 0,
    };

    // Get all sort preferences from localStorage (including siteStorage prefixed keys)
    try {
      Object.keys(localStorage)
        .filter((k) => k.includes("_grid_sort_user_pref_"))
        .forEach((key) => {
          // Extract sortType and gridId from key: {prefix}{sortType}_grid_sort_user_pref_{gridId}
          // The key may have a siteStorage prefix like "https://example.com::"
          const match = key.match(/_grid_sort_user_pref_(.+)$/);
          if (match) {
            const gridId = match[1];
            // Extract sortType - it's between any :: prefix and _grid_sort_user_pref_
            const sortTypeMatch = key.match(
              /(?:::)?([^:]+)_grid_sort_user_pref_/,
            );
            const sortType = sortTypeMatch ? sortTypeMatch[1] : "unknown";
            const prefKey = `${sortType}/${gridId}`;
            diagnostics.preferences[prefKey] = localStorage.getItem(key);
          }
        });
    } catch (e) {
      diagnostics.localStorageError = e.message;
    }

    if (this.debugMode) {
      console.table(diagnostics.scriptsLoaded);
      console.table(diagnostics.readinessState);
      console.table(diagnostics.preferences);
      console.log("[HrefSort] Full diagnostics:", diagnostics);

      if (this.allowedSorts) {
        console.log(
          "[HrefSort] Allowed sorts configuration:",
          this.allowedSorts,
        );
      }
    }

    return diagnostics;
  },
};

// =============================================================================
// AUTO-INITIALIZATION
// =============================================================================
// Initialize as soon as DOM is ready. We use DOMContentLoaded if still loading,
// otherwise initialize immediately for dynamically loaded scripts.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.HrefSortInterceptor.init();
  });
} else {
  window.HrefSortInterceptor.init();
}
